// MaturaGeometricTermClosedTask.js
// Zadanie maturalne zamkniete: z danych a1 i a2 obliczamy wskazany wyraz ciagu geometrycznego.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    [x, y] = [y, x % y];
  }
  return x || 1;
}

function makeRational(num, den = 1) {
  if (den < 0) return makeRational(-num, -den);
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function multiplyRational(a, b) {
  return makeRational(a.num * b.num, a.den * b.den);
}

function rationalKey(value) {
  return `${value.num}/${value.den}`;
}

function rationalLatex(value) {
  if (value.den === 1) return String(value.num);
  const sign = value.num < 0 ? "-" : "";
  return `${sign}\\frac{${Math.abs(value.num)}}{${value.den}}`;
}

function ordinalText(n) {
  if (n === 3) return "Trzeci";
  if (n === 4) return "Czwarty";
  return `${n}.`;
}

function uniqueRationals(values) {
  const seen = new Set();
  const out = [];
  values.forEach((value) => {
    const r = makeRational(value.num, value.den);
    const key = rationalKey(r);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(r);
    }
  });
  return out;
}

function generateMay2025Original() {
  const a1 = makeRational(27);
  const a2 = makeRational(9);
  const a3 = makeRational(3);
  const a4 = makeRational(1);
  const ratio = makeRational(1, 3);
  const values = [
    makeRational(1, 3),
    makeRational(1),
    makeRational(3),
    makeRational(729),
  ];
  const options = values.map((value, idx) => ({
    label: LETTERS[idx],
    value,
    latex: rationalLatex(value),
    isCorrect: idx === 1,
  }));

  return {
    type: "MaturaGeometricTermClosedTask",
    data: {
      a1,
      a2,
      a3,
      a4,
      ratio,
      targetIndex: 4,
      options,
      correctIndex: 1,
      maxPoints: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 16,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025Original();
  }

  const ratio = pick([
    makeRational(1, 3),
    makeRational(1, 2),
    makeRational(2, 1),
    makeRational(3, 1),
  ]);
  const k = randInt(1, ratio.den === 1 ? 5 : 6);
  const a1 =
    ratio.den === 1
      ? makeRational(k, 1)
      : makeRational(k * ratio.den * ratio.den * ratio.den, 1);

  const a2 = multiplyRational(a1, ratio);
  const a3 = multiplyRational(a2, ratio);
  const a4 = multiplyRational(a3, ratio);
  const targetIndex = 4;
  const correctValue = a4;

  const distractors =
    ratio.den === 1
      ? uniqueRationals([correctValue, a3, a2, ratio, a1])
      : uniqueRationals([
          correctValue,
          a3,
          ratio,
          makeRational(a1.num * a1.num, 1),
          a2,
        ]);

  let optionValues = distractors;
  let delta = 1;
  while (optionValues.length < 4) {
    optionValues = uniqueRationals([
      ...optionValues,
      makeRational(correctValue.num + delta * correctValue.den, correctValue.den),
      makeRational(correctValue.num - delta * correctValue.den, correctValue.den),
    ]);
    delta += 1;
  }

  const shuffled = shuffle(optionValues.slice(0, 4));
  const correctKey = rationalKey(correctValue);
  const options = shuffled.map((value, idx) => ({
    label: LETTERS[idx],
    value,
    latex: rationalLatex(value),
    isCorrect: rationalKey(value) === correctKey,
  }));

  return {
    type: "MaturaGeometricTermClosedTask",
    data: {
      a1,
      a2,
      a3,
      a4,
      ratio,
      targetIndex,
      options,
      correctIndex: options.findIndex((opt) => opt.isCorrect),
      maxPoints: 1,
    },
  };
}

export function renderTask({ data }) {
  const { a1, a2, targetIndex, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        Dany jest ciąg geometryczny <InlineMath math={"(a_n)"} /> określony dla
        każdej liczby naturalnej <InlineMath math={"n\\geq1"} />, w którym{" "}
        <InlineMath math={`a_1=${rationalLatex(a1)}`} /> oraz{" "}
        <InlineMath math={`a_2=${rationalLatex(a2)}`} />.
      </p>

      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>

      <p>
        {ordinalText(targetIndex)} wyraz ciągu <InlineMath math={"(a_n)"} />{" "}
        jest równy:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 10 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={options.map((opt, idx) => ({
        value: idx,
        label: opt.label,
        textNode: opt.label,
      }))}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderExplanation({ data }) {
  const { a1, a2, a3, a4, ratio, targetIndex } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> W ciągu geometrycznym iloraz jest stały:
      </p>
      <BlockMath
        math={`q=\\frac{a_2}{a_1}=\\frac{${rationalLatex(a2)}}{${rationalLatex(
          a1
        )}}=${rationalLatex(ratio)}`}
      />

      <p>
        <strong>Krok 2.</strong> Kolejne wyrazy otrzymujemy przez mnożenie przez{" "}
        <InlineMath math={"q"} />:
      </p>
      <BlockMath
        math={`a_3=a_2\\cdot q=${rationalLatex(a2)}\\cdot${rationalLatex(
          ratio
        )}=${rationalLatex(a3)}`}
      />
      <BlockMath
        math={`a_4=a_3\\cdot q=${rationalLatex(a3)}\\cdot${rationalLatex(
          ratio
        )}=${rationalLatex(a4)}`}
      />

      <p>
        Zatem <InlineMath math={`a_{${targetIndex}}=${rationalLatex(a4)}`} />.
      </p>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />
    </div>
  );
}
